import { 
    AcademicReportSubmissionService as SVC, 
    SubmissionOpportunity, 
    SubmissionType,
    AuthenticationService,
    AppVersionService } from '../domain/data-access-interfaces';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AppSettings } from 'app/app-settings';
import { Result } from 'app/domain';
import { Observable, ReplaySubject, Subject, firstValueFrom } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';

@Injectable()
export class AcademicReportSubmissionService implements SVC, OnDestroy {
    private _subjects: Map<SubmissionType, Subject<SubmissionOpportunity>>;
    private _destroyed$ = new Subject<void>();

    constructor(
        private http: HttpClient, 
        private appSettings: AppSettings,
        private auth: AuthenticationService,
        private appVersion: AppVersionService
    )  {
        let allTypes = [
            SubmissionType.ConditionChangeFTE,
            SubmissionType.CountyFTE,
            SubmissionType.ThemesProjectsActivitiesANRMMP,
            SubmissionType.ThemesProjectsActivitiesCampusMMP
        ];

        this._subjects = new Map(
            allTypes.map(t => 
            [
                t,
                new ReplaySubject<SubmissionOpportunity>(1)
            ] as [SubmissionType, Subject<SubmissionOpportunity>]));

        allTypes.forEach(t => this._load(t));

        this.auth.currentIdentity.pipe(takeUntil(this._destroyed$)).subscribe(identity => { 
            identity.match(
                () => {}, // Avoids loading when the user logs out, which would cause a 401 error
                _ => allTypes.forEach(t => this._load(t))
            );
        });

        this.appVersion.clockChanged$.pipe(takeUntil(this._destroyed$)).subscribe(() => 
            allTypes.forEach(t => this._load(t)));

        this.appVersion.reviewDatesChanged$.pipe(takeUntil(this._destroyed$)).subscribe(() =>
            allTypes.forEach(t => this._load(t)));
    }

    public ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public getSubmissionOpportunity(type:SubmissionType): Observable<SubmissionOpportunity> {
        return this._subjects.get(type).asObservable();
    }

    public submit(type:SubmissionType, ffyId: number, date?: string): Promise<Result<null,string>>{
        let params = new HttpParams();
        if (date) {
            params = params.set('now', date);
        }

        return firstValueFrom(this.http.post(this.appSettings.APIURL + `/academic_report_submission/${type}/${ffyId}`, null, { params })
        .pipe(map(json =>
            Result.fromJson(
                json,
                _ => {
                    this._load(type);
                    return null;
                },
                (errorMsg: string) => errorMsg))));
    }

    private _load(type:SubmissionType): void {
        let params = new HttpParams();
        const simulated = this.appVersion.serverInfo?.simulatedDate;
        if (simulated) {
            params = params.set('now', simulated);
        }

        this.http.get(this.appSettings.APIURL + `/academic_report_submission/${type}`, { params })
        .pipe(
            map(SubmissionOpportunity.fromJson),
            takeUntil(this._destroyed$)
        )
        .subscribe(o => {
            this._subjects.get(type).next(o);
        });
    }
}
